import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Send from '@mui/icons-material/Send';
import { ReviewComment } from '../../types/Call';

interface ReviewCommentsProps {
  comments: ReviewComment[];
  onAddComment: (comment: string) => void;
}

export const ReviewComments: React.FC<ReviewCommentsProps> = ({ comments, onAddComment }) => {
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newComment.trim()) return;

    setIsSubmitting(true);
    try {
      await onAddComment(newComment.trim());
      setNewComment('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  return (
    <Box p={3}>
        {/* Add new comment form */}
        <Box component="form" onSubmit={handleSubmit} mb={3}>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Enter your review comment..."
            disabled={isSubmitting}
            sx={{ mb: 2 }}
          />
          <Box display="flex" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              disabled={!newComment.trim() || isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : <Send />}
            >
              {isSubmitting ? 'Adding...' : 'Add Comment'}
            </Button>
          </Box>
        </Box>

        {/* Comments list */}
        <Stack spacing={2} divider={<Divider />}>
          {comments.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                No comments yet
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Be the first to add a review comment
              </Typography>
            </Box>
          ) : (
            comments
              .slice()
              .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
              .map((comment) => {
                const isRetracted = !!comment.retractedDate;
                return (
                  <Box key={comment.commentId} display="flex" gap={2}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                      {comment.userName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                        <Typography variant="subtitle2">
                          {comment.userName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(comment.createdDate)}
                        </Typography>
                        {/* Status carries its meaning in the label text, not in colour alone. */}
                        {comment.amendsCommentId && (
                          <Chip size="small" variant="outlined" label="Amends an earlier comment" />
                        )}
                        {isRetracted && (
                          <Chip size="small" color="warning" variant="outlined" label="Retracted" />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        color={isRetracted ? 'text.secondary' : 'text.primary'}
                        sx={{ textDecoration: isRetracted ? 'line-through' : 'none' }}
                      >
                        {comment.comment}
                      </Typography>
                      {isRetracted && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                          Withdrawn by {comment.retractedBy || 'unknown'}
                          {comment.retractionReason ? ` — ${comment.retractionReason}` : ''}. The
                          original text is retained as part of the record.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })
          )}
        </Stack>
      </Box>
  );
};